import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FLOWS_ENUM } from 'src/common/enums';
import {
  selectCurrentFlow,
  selectFlowSteps,
} from 'src/store/slices/flow/flowSelectors';
import type { Step } from 'src/types/flow';
import { selectConfigExpressSettingsName } from 'src/store/slices/config/configSelectors';

const PORTAL_SUFFIX = 'XCP Portal';

const FLOW_NAMES: Record<string, string> = {
  [FLOWS_ENUM.APPLY_LICENSE]: 'Apply',
  [FLOWS_ENUM.UPDATE_CERTIFICATE]: 'Update Certificate',
  [FLOWS_ENUM.RENEW_LICENSE]: 'Renew',
  [FLOWS_ENUM.BULK_RENEWAL]: 'Bulk Renewal',
  [FLOWS_ENUM.PAY_TOT]: 'Tax Filing',
  [FLOWS_ENUM.ADMIN_UPDATE]: 'Admin Update',
};

const formatWithPortalSuffix = (title: string) => `${title} | ${PORTAL_SUFFIX}`;

const getRootTitle = (placeDisplayName?: string) => {
  const place = placeDisplayName?.trim();
  return place ? formatWithPortalSuffix(place) : PORTAL_SUFFIX;
};

const getStaticPageTitles = (): Record<string, string> => ({
  dashboard: formatWithPortalSuffix('Dashboard'),
  'email-check': formatWithPortalSuffix('Check Email'),
  'create-account': formatWithPortalSuffix('Create Account'),
  'account-update': formatWithPortalSuffix('Account Update'),
  'all-permits': formatWithPortalSuffix('All Permits'),
  'single-permit': formatWithPortalSuffix('Permit Details'),
  'apply-success': formatWithPortalSuffix('Application Submitted'),
  'renew-success': formatWithPortalSuffix('Renewal Complete'),
  'update-success': formatWithPortalSuffix('Update Complete'),
  'tax-success': formatWithPortalSuffix('Tax Filing Complete'),
  'admin-update-success': formatWithPortalSuffix('Update Complete'),
  'payment-redirect': formatWithPortalSuffix('Payment'),
  'payment-completed': formatWithPortalSuffix('Payment Completed'),
  'payment-success': formatWithPortalSuffix('Payment Successful'),
  'payment-pending': formatWithPortalSuffix('Payment Pending'),
  'payment-error': formatWithPortalSuffix('Payment Error'),
  'payment-cybersource': formatWithPortalSuffix('Payment'),
  'payment-cardconnect': formatWithPortalSuffix('Payment'),
  error: formatWithPortalSuffix('Error'),
});

const useDocumentTitle = () => {
  const location = useLocation();
  const placeDisplayName = useSelector(
    selectConfigExpressSettingsName
  ) as string;
  const currentFlow = useSelector(selectCurrentFlow) as string | undefined;
  const steps = useSelector(selectFlowSteps) as Step[] | undefined;

  useEffect(() => {
    // segments[0] = city identifier (e.g. 'boston-str-portal')
    // segments[1] = main page segment (e.g. 'apply-property')
    // segments[2] = optional dynamic param (e.g. application number)
    const segments = location.pathname.split('/').filter(Boolean);
    const pageSegment = segments[1] ?? '';

    const matchedStep = steps?.find((step) => step.key === pageSegment);
    if (matchedStep && currentFlow) {
      const flowName = FLOW_NAMES[currentFlow] ?? currentFlow;
      document.title = formatWithPortalSuffix(
        `${matchedStep.stepName} — ${flowName}`
      );
      return;
    }

    if (pageSegment === '') {
      document.title = getRootTitle(placeDisplayName);
      return;
    }

    const staticTitle = getStaticPageTitles()[pageSegment];
    if (staticTitle) {
      document.title = staticTitle;
      return;
    }

    document.title = PORTAL_SUFFIX;
  }, [location.pathname, currentFlow, steps, placeDisplayName]);
};

export default useDocumentTitle;
